import React, { useMemo } from 'react';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import {
  FormControl,
  Select,
  MenuItem,
  Typography,
  ListSubheader,
  Tooltip,
  Box,
} from '@mui/material';
import { CustomAutocomplete } from '../../../components/Infra/CustomAutocomplete/CustomAutocomplete';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningIcon from '@mui/icons-material/Warning';
import { EditableCell } from '../components/CellComponents/EditableCell';
import {
  DataFeedRowData,
  DataFeedInstrumentType,
} from '../../../types/generatedTypes';
import {
  StatusIndicator,
  StatusType,
} from '../../../components/Infra/StatusIndicator';
import { getCellError } from '../utils/danelErrorUtils';
import CellErrorHighlight from '../../../components/ErrorDisplay/CellErrorHighlight';
import { getNumericStringSortComparator, createDateSortComparator, isDateField, detectDateFormat } from '../../../utils/sortComparators';
import {
  ConfigurationTemplate,
  getUITypeFromBackend,
} from '../../../components/DataFeed/Configuration/types';
import { ViewMode } from '../../../components/DataFeed/ViewModeToggle/types';
import { isMandatoryFieldEmpty, isAwardFieldMissing } from '../utils/mandatoryFieldsUtils';
import { WarningType } from '../components/CellComponents/types';
import {
  CURRENCIES,
  getCurrencyDisplay,
} from '../../../components/DataFeed/Configuration/components/ConfigurationSections/CurrencySection/currencyData';
import { RowType } from '../../../types/rowType';


const NON_EDITABLE_PROCESSED_COLUMNS = new Set([
  'ASSET_NAME',
  'SYMBOL',
  'ILLIO_SOURCE_ID',
  'CLIENT_NAME',
  'CUSTODIAN',
  'SETTLEMENT_ACCOUNT',
  'FX_SPOT_TYPE',
  'BUY_QUANTITY',
  'SELL_QUANTITY',
  'BUY_CURRENCY',
  '_instrument_type',
  'uploadStatus',
  'TRANSACTION_DATE',
  'LINKED_ACCOUNT',
  'ACCOUNT_NUMBER',
]);

const ACCOUNT_NUMBER_COLUMN: GridColDef = {
  field: 'ACCOUNT_NUMBER',
  headerName: 'Account Number',
  minWidth: 160,
  editable: false,
  sortable: true,
  align: 'left',
  headerAlign: 'left',
};

const LINKED_ACCOUNT_COLUMN: GridColDef = {
  field: 'LINKED_ACCOUNT',
  headerName: 'Linked Account',
  minWidth: 200,
  editable: false,
  sortable: true,
  align: 'left',
  headerAlign: 'left',
};

interface LinkedAccountData {
  linkedAccountMap: Map<string, string>;
  hasLinkedAccounts: boolean;
  accountNumberColumn: string | null;
}

interface UseDanelColumnsProps {
  rows: DataFeedRowData[];
  processRowUpdate: (
    newRow: DataFeedRowData,
    oldRow: DataFeedRowData
  ) => Promise<DataFeedRowData>;
  autosizeColumns: boolean;
  viewMode: ViewMode;
  appliedTemplateConfig: ConfigurationTemplate | null;
  readOnly?: boolean;
  hidePreviewColumns?: boolean;
  allowCellEditing?: boolean;
  rowsType?: RowType;
  linkedAccountData?: LinkedAccountData;
}

// All 16 specific DataFeedInstrumentType values with categorization
const DATAFEED_INSTRUMENT_TYPE_OPTIONS: Array<{
  value: DataFeedInstrumentType;
  label: string;
  category: string;
}> = [
  // Basic Types
  { value: 'Equities', label: 'Equities', category: 'Basic' },
  { value: 'Funds & ETFs', label: 'Funds & ETFs', category: 'Basic' },
  { value: 'FixedIncome', label: 'Fixed Income', category: 'Basic' },
  { value: 'Dividend', label: 'Dividend', category: 'Basic' },
  { value: 'Option', label: 'Option', category: 'Basic' },

  // FX Types
  { value: 'FX Spot Buy', label: 'FX Spot Buy', category: 'FX' },
  { value: 'FX Spot Sell', label: 'FX Spot Sell', category: 'FX' },

  // Transaction Types
  { value: 'Deposit', label: 'Deposit', category: 'Transactions' },
  { value: 'Withdraw', label: 'Withdraw', category: 'Transactions' },
  { value: 'Transfer', label: 'Transfer', category: 'Transactions' },

  // Cash Adjustments
  {
    value: 'Cash Adjustment - Custodian Fee',
    label: 'Cash Adjustment - Custodian Fee',
    category: 'Cash Adjustments',
  },
  {
    value: 'Cash Adjustment - Management Fee',
    label: 'Cash Adjustment - Management Fee',
    category: 'Cash Adjustments',
  },
  {
    value: 'Cash Adjustment - Withholding Tax',
    label: 'Cash Adjustment - Withholding Tax',
    category: 'Cash Adjustments',
  },
  {
    value: 'Cash Adjustment - Interest',
    label: 'Cash Adjustment - Interest',
    category: 'Cash Adjustments',
  },
  {
    value: 'Cash Adjustment - Performance Fee',
    label: 'Cash Adjustment - Performance Fee',
    category: 'Cash Adjustments',
  },
  {
    value: 'Cash Adjustment - Misc Charge',
    label: 'Cash Adjustment - Misc Charge',
    category: 'Cash Adjustments',
  },
  {
    value: 'Cash Adjustment - P&L Adjustment',
    label: 'Cash Adjustment - P&L Adjustment',
    category: 'Cash Adjustments',
  },
  {
    value: 'Cash Adjustment - Income Adjustment',
    label: 'Cash Adjustment - Income Adjustment',
    category: 'Cash Adjustments',
  },
];

const BUY_SELL_OPTIONS = [
  { value: 'BUY', label: 'Buy' },
  { value: 'SELL', label: 'Sell' },
] as const;

export const useDanelColumns = ({
  rows,
  processRowUpdate,
  autosizeColumns,
  viewMode,
  appliedTemplateConfig,
  readOnly = false,
  hidePreviewColumns = false,
  allowCellEditing = false,
  rowsType,
  linkedAccountData,
}: UseDanelColumnsProps) => {
  // Helper to check if field was edited and value differs from original
  const getEditInfo = (
    row: DataFeedRowData,
    field: string,
    currentValue: unknown
  ): { isEdited: boolean; originalValue: unknown } => {
    const originalValues = (row as Record<string, unknown>)._original_values as
      | Record<string, unknown>
      | undefined;
    if (!originalValues || !(field in originalValues)) {
      return { isEdited: false, originalValue: undefined };
    }
    const originalValue = originalValues[field];
    // Only consider edited if current value differs from original
    const isEdited = String(currentValue ?? '') !== String(originalValue ?? '');
    return { isEdited, originalValue };
  };

  // Helper to get warning type from _field_errors
  const getWarningType = (
    row: DataFeedRowData,
    fieldName: string
  ): WarningType => {
    const fieldErrors = (row as Record<string, unknown>)._field_errors as
      | Record<string, string>
      | undefined;
    if (fieldErrors && fieldErrors[fieldName]) {
      const errorType = fieldErrors[fieldName];
      if (errorType === 'transform_error') {
        return errorType;
      }
    }
    return 'missing';
  };

  const renderCellWithErrorHighlight = useMemo(
    // eslint-disable-next-line react/display-name
    () => (params: GridRenderCellParams, fieldName: string) => {
      const cellError = getCellError(
        params.row.id,
        fieldName,
        {}, // No in-memory upload errors - using row.upload_status from backend
        rows
      );
      const hasError = !!cellError;

      return (
        <CellErrorHighlight
          value={params.value}
          hasError={hasError}
          fieldName={fieldName}
          errorInfo={
            cellError
              ? {
                  title: cellError.title,
                  message: cellError.message,
                  affected_field: cellError.affected_field,
                  field_value: cellError.field_value
                    ? String(cellError.field_value)
                    : undefined,
                  available_instruments:
                    'available_instruments' in cellError
                      ? cellError.available_instruments
                      : undefined,
                }
              : undefined
          }
        />
      );
    },
    [rows]
  );

  const instrumentTypeColumn = useMemo(
    (): GridColDef => ({
      field: 'instrumentType',
      headerName: 'Instrument Type',
      minWidth: 250,
      ...(autosizeColumns ? {} : { flex: 1 }),
      editable: !readOnly,
      sortable: true,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params: GridRenderCellParams) => {
        // For positions uploads, always show "Position" as instrument type
        // Otherwise auto-populate from _instrument_type if available (merged from processed rows)
        const currentValue = rowsType === RowType.ROW_TYPE_POSITION
          ? 'Position'
          : (params.value || params.row._instrument_type || '');

        // Positions: fixed type, render as text (Position not in transaction-type options)
        if (rowsType === RowType.ROW_TYPE_POSITION) {
          return (
            <Typography sx={{ fontSize: '0.875rem' }}>
              Position
            </Typography>
          );
        }

        // Read-only mode: render plain text
        if (readOnly) {
          return (
            <Typography sx={{ fontSize: '0.875rem' }}>
              {currentValue}
            </Typography>
          );
        }

        // Group options by category
        const groupedOptions = DATAFEED_INSTRUMENT_TYPE_OPTIONS.reduce(
          (acc, opt) => {
            if (!acc[opt.category]) acc[opt.category] = [];
            acc[opt.category].push(opt);
            return acc;
          },
          {} as Record<string, typeof DATAFEED_INSTRUMENT_TYPE_OPTIONS>
        );

        return (
          <FormControl fullWidth size="small">
            <Select
              value={currentValue}
              onChange={e => {
                const selectedValue = e.target.value;
                const updatedRow = {
                  ...params.row,
                  instrumentType: selectedValue,
                };
                processRowUpdate(updatedRow, params.row);
              }}
              displayEmpty
              variant="standard"
              sx={{ width: '100%' }}
            >
              <MenuItem value="">
                <em>Select type</em>
              </MenuItem>
              {Object.entries(groupedOptions).map(([category, options]) => [
                <ListSubheader key={`header-${category}`}>
                  {category}
                </ListSubheader>,
                ...options.map((opt, idx) => (
                  <MenuItem key={`${opt.value}-${idx}`} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                )),
              ])}
            </Select>
          </FormControl>
        );
      },
    }),
    [processRowUpdate, autosizeColumns, readOnly, rowsType]
  );

  const currencyColumn = useMemo(
    (): GridColDef => ({
      field: 'CURRENCY',
      headerName: 'Currency',
      minWidth: 150,
      editable: false,
      sortable: true,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params: GridRenderCellParams) => {
        const currentValue = params.value || '';
        const instrumentType = params.row._instrument_type;
        const uiType = instrumentType ? getUITypeFromBackend(instrumentType) : null;
        const isMandatoryEmpty = isMandatoryFieldEmpty(
          'CURRENCY',
          currentValue,
          uiType
        );
        const { isEdited, originalValue } = getEditInfo(
          params.row,
          'CURRENCY',
          currentValue
        );

        if (!allowCellEditing) {
          return (
            <EditableCell
              showWarning={isMandatoryEmpty}
              isEdited={false}
              originalValue={undefined}
              warningType={getWarningType(params.row, 'CURRENCY')}
            >
              <Typography sx={{ fontSize: '0.875rem' }}>
                {currentValue}
              </Typography>
            </EditableCell>
          );
        }

        return (
          <EditableCell
            showWarning={isMandatoryEmpty}
            isEdited={isEdited}
            originalValue={originalValue}
            fullWidth
            warningType={getWarningType(params.row, 'CURRENCY')}
          >
            <CustomAutocomplete
              fullWidth
              options={CURRENCIES}
              getOptionLabel={option => getCurrencyDisplay(option.code)}
              value={CURRENCIES.find(c => c.code === currentValue)}
              onChange={(_, newValue) => {
                if (newValue) {
                  const updatedRow = { ...params.row, CURRENCY: newValue.code };
                  processRowUpdate(updatedRow, params.row);
                }
              }}
              disableClearable
              textFieldProps={{ variant: 'standard' }}
            />
          </EditableCell>
        );
      },
    }),
    [processRowUpdate, allowCellEditing]
  );

  const buySellColumn = useMemo(
    (): GridColDef => ({
      field: 'BUY_SELL',
      headerName: 'Buy/Sell',
      minWidth: 100,
      editable: false,
      sortable: true,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params: GridRenderCellParams) => {
        const currentValue = params.value || '';
        const instrumentType = params.row._instrument_type;
        const uiType = instrumentType ? getUITypeFromBackend(instrumentType) : null;
        const isMandatoryEmpty = isMandatoryFieldEmpty(
          'BUY_SELL',
          currentValue,
          uiType
        );
        const { isEdited, originalValue } = getEditInfo(
          params.row,
          'BUY_SELL',
          currentValue
        );

        if (!allowCellEditing) {
          return (
            <EditableCell
              showWarning={isMandatoryEmpty}
              isEdited={false}
              originalValue={undefined}
              warningType={getWarningType(params.row, 'BUY_SELL')}
            >
              <Typography sx={{ fontSize: '0.875rem' }}>
                {currentValue}
              </Typography>
            </EditableCell>
          );
        }

        return (
          <EditableCell
            showWarning={isMandatoryEmpty}
            isEdited={isEdited}
            originalValue={originalValue}
            fullWidth
            warningType={getWarningType(params.row, 'BUY_SELL')}
          >
            <FormControl fullWidth size="small">
              <Select
                value={currentValue}
                onChange={e => {
                  const updatedRow = {
                    ...params.row,
                    BUY_SELL: e.target.value,
                  };
                  processRowUpdate(updatedRow, params.row);
                }}
                variant="standard"
              >
                {BUY_SELL_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </EditableCell>
        );
      },
    }),
    [processRowUpdate, allowCellEditing]
  );

  const settlementDateColumn = useMemo(
    (): GridColDef => ({
      field: 'SETTLEMENT_DATE',
      headerName: 'Settlement Date',
      minWidth: 160,
      editable: false,
      sortable: true,
      // Backend always returns SETTLEMENT_DATE in ISO format (YYYY-MM-DD)
      getSortComparator: createDateSortComparator('YYYY-MM-DD'),
      align: 'left',
      headerAlign: 'left',
      renderCell: (params: GridRenderCellParams) => {
        const currentValue = params.value || '';
        const instrumentType = params.row._instrument_type;
        const uiType = instrumentType ? getUITypeFromBackend(instrumentType) : null;
        const isMandatoryEmpty = isMandatoryFieldEmpty(
          'SETTLEMENT_DATE',
          currentValue,
          uiType
        );
        const { isEdited, originalValue } = getEditInfo(
          params.row,
          'SETTLEMENT_DATE',
          currentValue
        );

        if (!allowCellEditing) {
          return (
            <EditableCell
              showWarning={isMandatoryEmpty}
              isEdited={false}
              originalValue={undefined}
              warningType={getWarningType(params.row, 'SETTLEMENT_DATE')}
            >
              <Typography sx={{ fontSize: '0.875rem' }}>
                {currentValue}
              </Typography>
            </EditableCell>
          );
        }

        // Parse date - try common formats
        const parsedDate = currentValue
          ? dayjs(currentValue, [
              'M/D/YYYY',
              'MM/DD/YYYY',
              'DD-MM-YYYY',
              'YYYY-MM-DD',
            ])
          : null;
        const validDate = parsedDate?.isValid() ? parsedDate : null;

        return (
          <EditableCell
            showWarning={isMandatoryEmpty}
            isEdited={isEdited}
            originalValue={originalValue}
            fullWidth
            warningType={getWarningType(params.row, 'SETTLEMENT_DATE')}
          >
            <DatePicker
              value={validDate}
              onAccept={newValue => {
                if (newValue && !newValue.isValid()) {
                  return; // Only accept valid dates
                }
                const formattedDate = newValue
                  ? newValue.format('YYYY-MM-DD')
                  : '';
                const updatedRow = {
                  ...params.row,
                  SETTLEMENT_DATE: formattedDate,
                };
                processRowUpdate(updatedRow, params.row);
              }}
              slotProps={{
                textField: {
                  variant: 'standard',
                  size: 'small',
                  fullWidth: true,
                },
              }}
              format="YYYY-MM-DD"
            />
          </EditableCell>
        );
      },
    }),
    [processRowUpdate, allowCellEditing]
  );

  const uploadStatusColumn = useMemo(
    (): GridColDef => ({
      field: 'uploadStatus',
      headerName: '',
      minWidth: 40,
      ...(autosizeColumns ? {} : { flex: 0.8 }),
      editable: false,
      sortable: true,
      align: 'left',
      headerAlign: 'left',
      valueGetter: (value, row) => {
        const dbUploadStatus = row.upload_status;

        if (dbUploadStatus) {
          if (dbUploadStatus.status === 'success') return 3;
          if (dbUploadStatus.status === 'illio_warning') return 2.5;
          if (dbUploadStatus.status === 'error') return 2;
        }

        return 1; // Not uploaded
      },
      renderCell: (params: GridRenderCellParams) => {
        const dbUploadStatus = params.row.upload_status;
        const status: StatusType =
          dbUploadStatus?.status === 'success'
            ? 'success'
            : dbUploadStatus?.status === 'illio_warning'
              ? 'illio_warning'
              : dbUploadStatus?.status === 'error'
                ? 'error'
                : 'not_uploaded';

        return (
          <StatusIndicator
            status={status}
            tooltip={
              status === 'error' || status === 'illio_warning'
                ? dbUploadStatus?.message
                : undefined
            }
          />
        );
      },
    }),
    [autosizeColumns]
  );

  const matchReasonColumn = useMemo(
    (): GridColDef => ({
      field: '_match_reason',
      headerName: 'Match Reason',
      width: 100,
      editable: false,
      sortable: true,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return null;
        }
        return (
          <Tooltip title={params.value} arrow placement="top">
            <InfoOutlinedIcon
              sx={{ color: 'text.secondary', cursor: 'pointer' }}
            />
          </Tooltip>
        );
      },
    }),
    []
  );

  const linkedAccountColumn = useMemo((): GridColDef | null => {
    if (
      !linkedAccountData?.hasLinkedAccounts ||
      !linkedAccountData.accountNumberColumn
    ) {
      return null;
    }
    const { linkedAccountMap, accountNumberColumn } = linkedAccountData;
    return {
      field: '__linked_account__',
      headerName: 'Linked Account',
      minWidth: 200,
      editable: false,
      sortable: true,
      align: 'left' as const,
      headerAlign: 'left' as const,
      valueGetter: (_value: unknown, row: DataFeedRowData) => {
        const accountNumber = row[accountNumberColumn];
        if (accountNumber == null) return '';
        return linkedAccountMap.get(String(accountNumber)) ?? '';
      },
    };
  }, [linkedAccountData]);

  const columns = useMemo(() => {
    if (rows.length === 0) return [];

    // RAW MODE - keep existing behavior
    if (viewMode === 'raw') {
      const firstRow = rows[0];
      const dynamicColumns: GridColDef[] = [
        uploadStatusColumn,
        ...(hidePreviewColumns ? [] : [instrumentTypeColumn]),
        ...(hidePreviewColumns ? [] : [matchReasonColumn]),
        ...(linkedAccountColumn ? [linkedAccountColumn] : []),
      ];

      // Add the rest of the columns
      // Filter out internal fields (starting with '_') and special fields
      const dynamicColumnKeys = Object.keys(firstRow).filter(
        key =>
          key !== 'id' &&
          key !== 'instrumentType' &&
          key !== 'Instrument Type' && // Always hide backend Instrument Type (use editable dropdown instead)
          key !== 'upload_status' &&
          !key.startsWith('_') // Hide all internal fields starting with underscore
      );

      const colFormats: Record<string, string | null> = {};
      for (const key of dynamicColumnKeys) {
        colFormats[key] = detectDateFormat(rows.map((r) => r[key]));
      }

      const otherColumns = dynamicColumnKeys.map(key => ({
          field: key,
          headerName: key,
          ...(autosizeColumns ? {} : { flex: 1 }),
          minWidth: 100,
          editable: false,
          sortable: true,
          align: 'left' as const,
          headerAlign: 'left' as const,
          getSortComparator: colFormats[key] ? createDateSortComparator(colFormats[key]!) : getNumericStringSortComparator,
          renderCell: (params: GridRenderCellParams) =>
            renderCellWithErrorHighlight(params, key),
        }));

      return [...dynamicColumns, ...otherColumns];
    }

    // PROCESSED MODE - generate columns from configuration or row data (for positions)
    if (viewMode === 'processed') {
      // If no processed rows, return empty columns array
      if (rows.length === 0) {
        return [];
      }

      // Positions: no template — build columns from row keys
      if (rowsType === RowType.ROW_TYPE_POSITION && !appliedTemplateConfig) {
        const firstRow = rows[0];
        const POSITION_COLUMN_ORDER: Array<{ key: string; title: string }> = [
          { key: 'uploadStatus', title: '' },
          { key: '_instrument_type', title: 'Instrument Type' },
          { key: 'ASSET_NAME', title: 'Asset Name' },
          { key: 'SYMBOL', title: 'Symbol' },
          { key: 'ILLIO_SOURCE_ID', title: 'Illio Source ID' },
          { key: 'PORTFOLIO_ID', title: 'Portfolio' },
          { key: 'TRANSACTION_DATE', title: 'Transaction Date' },
          { key: 'QUANTITY', title: 'Quantity' },
          { key: 'PRICE', title: 'Price' },
          { key: 'CURRENCY', title: 'Currency' },
          { key: 'NOTE', title: 'Note' },
          { key: 'SETTLEMENT_ACCOUNT', title: 'Settlement Account' },
        ];
        const POSITION_HIDDEN_COLUMNS = new Set([
          'ILLIO_SOURCE_DATA',
          'MULTIPLIER',
          'PORTFOLIO_ID',
          'CLIENT_NAME',
          'CUSTODIAN',
          'LINKED_ACCOUNT',
          'ACCOUNT_NUMBER',
        ]);
        const orderedKeys = POSITION_COLUMN_ORDER.map(({ key }) => key);
        const otherKeys = Object.keys(firstRow).filter(
          (key) =>
            key !== 'id' &&
            key !== 'upload_status' &&
            !key.startsWith('_') &&
            !orderedKeys.includes(key) &&
            !POSITION_HIDDEN_COLUMNS.has(key)
        );
        const metadataColumns: GridColDef[] = [
          uploadStatusColumn,
          LINKED_ACCOUNT_COLUMN,
          ACCOUNT_NUMBER_COLUMN,
          {
            field: '_instrument_type',
            headerName: 'Instrument Type',
            minWidth: 150,
            editable: false,
            sortable: true,
            align: 'left',
            headerAlign: 'left',
            renderCell: (params: GridRenderCellParams) => (
              <Typography sx={{ fontSize: '0.875rem' }}>
                {params.value ?? 'Position'}
              </Typography>
            ),
          },
        ];
        const dataColumns: GridColDef[] = [
          ...POSITION_COLUMN_ORDER.filter(({ key }) => key !== 'uploadStatus' && key !== '_instrument_type'),
          ...otherKeys.map((key) => ({ key, title: key })),
        ]
          .filter(({ key }) => rows.some((r) => key in r))
          .map(({ key, title }) => {
            if (key === 'CURRENCY') {
              return { ...currencyColumn, headerName: title };
            }
            return {
              field: key,
              headerName: title,
              minWidth: 120,
              ...(autosizeColumns ? {} : { flex: 1 }),
              editable:
                allowCellEditing && !NON_EDITABLE_PROCESSED_COLUMNS.has(key),
              sortable: true,
              align: 'left' as const,
              headerAlign: 'left' as const,
              getSortComparator: getNumericStringSortComparator,
              ...(key === 'PRICE'
                ? {
                    renderCell: (params: GridRenderCellParams) =>
                      params.row._instrument_type === 'Deposit' ? null : (
                        <Typography sx={{ fontSize: '0.875rem' }}>
                          {params.value}
                        </Typography>
                      ),
                  }
                : {
                    renderCell: (params: GridRenderCellParams) => (
                      <Typography sx={{ fontSize: '0.875rem' }}>
                        {params.value}
                      </Typography>
                    ),
                  }),
            };
          });
        return [...metadataColumns, ...dataColumns];
      }

      // If no config (transactions without template), return empty columns array
      if (!appliedTemplateConfig) {
        return [];
      }

      // Extract unique instrument types from processed rows
      const uniqueInstrumentTypes = new Set<DataFeedInstrumentType>();
      rows.forEach(row => {
        if (row._instrument_type) {
          uniqueInstrumentTypes.add(
            row._instrument_type as DataFeedInstrumentType
          );
        }
      });

      // Metadata columns that should appear first
      const metadataColumns: GridColDef[] = [
        uploadStatusColumn,
        {
          field: '_instrument_type',
          headerName: 'Instrument Type',
          minWidth: 180,
          editable: false,
          sortable: true,
          align: 'left',
          headerAlign: 'left',
          renderCell: (params: GridRenderCellParams) => {
            const detectionError = params.row._detection_error;
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {detectionError && (
                  <Tooltip title={detectionError}>
                    <WarningIcon
                      sx={{ color: 'warning.main', fontSize: '1rem' }}
                    />
                  </Tooltip>
                )}
                <Typography sx={{ fontSize: '0.875rem' }}>
                  {params.value}
                </Typography>
              </Box>
            );
          },
        },
        LINKED_ACCOUNT_COLUMN,
        ACCOUNT_NUMBER_COLUMN,
      ];

      // Collect all sections from instrument configs that match the detected types
      const allSections = new Map<string, { title: string; order: number }>();
      let sectionOrder = 0;

      uniqueInstrumentTypes.forEach(instrumentType => {
        // Map DataFeedInstrumentType to UIInstrumentType
        const uiType = getUITypeFromBackend(instrumentType);

        // Find the instrument config in the template
        const instrumentConfig = appliedTemplateConfig.instrumentConfigs.find(
          config => config.instrumentType === uiType
        );

        if (instrumentConfig) {
          // Add all sections from this instrument config
          instrumentConfig.sections.forEach(section => {
            if (!allSections.has(section.id)) {
              allSections.set(section.id, {
                title:
                  section.id === 'ASSET_NAME' ? 'Asset Name' : section.title,
                order: sectionOrder++,
              });
            }
          });
        }
      });

      // Add ASSET_NAME column if it exists in processed rows
      if (rows.some(row => row.ASSET_NAME !== undefined)) {
        if (!allSections.has('ASSET_NAME')) {
          allSections.set('ASSET_NAME', {
            title: 'Asset Name',
            order: 0, // Show first after metadata columns
          });
        }
      }

      // Add SYMBOL column if it exists in processed rows
      if (rows.some(row => row.SYMBOL !== undefined)) {
        if (!allSections.has('SYMBOL')) {
          allSections.set('SYMBOL', {
            title: 'Symbol',
            order: 0, // Show first after metadata columns
          });
        }
      }

      // Add ILLIO_SOURCE_ID column if it exists in processed rows
      if (rows.some(row => row.ILLIO_SOURCE_ID !== undefined)) {
        if (!allSections.has('ILLIO_SOURCE_ID')) {
          allSections.set('ILLIO_SOURCE_ID', {
            title: 'Illio Source ID',
            order: 0,
          });
        }
      }

      // Add CLIENT_NAME column if it exists in processed rows
      if (rows.some(row => row.CLIENT_NAME !== undefined)) {
        if (!allSections.has('CLIENT_NAME')) {
          allSections.set('CLIENT_NAME', {
            title: 'Client Name',
            order: -2,
          });
        }
      }

      // Add CUSTODIAN column if it exists in processed rows
      if (rows.some(row => row.CUSTODIAN !== undefined)) {
        if (!allSections.has('CUSTODIAN')) {
          allSections.set('CUSTODIAN', {
            title: 'Custodian',
            order: -1,
          });
        }
      }

      // Auto-detect position-specific columns from processed row data
      if (rowsType === RowType.ROW_TYPE_POSITION) {
        const positionColumns: Array<{ key: string; title: string }> = [
          { key: 'TRANSACTION_DATE', title: 'Transaction Date' },
          { key: 'QUANTITY', title: 'Quantity' },
          { key: 'PRICE', title: 'Price' },
          { key: 'CURRENCY', title: 'Currency' },
          { key: 'NOTE', title: 'Note' },
          { key: 'SETTLEMENT_ACCOUNT', title: 'Settlement Account' },
        ];

        positionColumns.forEach(({ key, title }) => {
          if (rows.some(row => row[key] !== undefined)) {
            if (!allSections.has(key)) {
              allSections.set(key, { title, order: sectionOrder++ });
            }
          }
        });
      }

      // Generate data columns from sections
      const dataColumns: GridColDef[] = Array.from(allSections.entries())
        .filter(
          ([sectionId]) =>
            sectionId !== 'INSTRUMENT_IDENTIFIERS' &&
            sectionId !== 'SYMBOL_ISIN' &&
            sectionId !== 'DEPOSIT_WITHDRAW_TYPE' &&
            sectionId !== 'CLIENT_NAME' &&
            sectionId !== 'CUSTODIAN' &&
            sectionId !== 'FEE_TYPE' &&
            !(rowsType === RowType.ROW_TYPE_POSITION && sectionId === 'BUY_SELL') &&
            !(rowsType === RowType.ROW_TYPE_POSITION && sectionId === 'COMMISSION') &&
            !(rowsType === RowType.ROW_TYPE_POSITION && sectionId === 'OTHER_CHARGES')
        )
        .sort((a, b) => a[1].order - b[1].order)
        .map(([sectionId, sectionInfo]) => {
          // Use custom column definitions for specific sections
          if (sectionId === 'CURRENCY') {
            return { ...currencyColumn, headerName: sectionInfo.title };
          }
          if (sectionId === 'BUY_SELL') {
            return { ...buySellColumn, headerName: sectionInfo.title };
          }
          if (sectionId === 'SETTLEMENT_DATE') {
            return { ...settlementDateColumn, headerName: sectionInfo.title };
          }

          // Default column definition for other columns
          return {
            field: sectionId,
            headerName: sectionInfo.title,
            minWidth: 150,
            editable:
              allowCellEditing &&
              !NON_EDITABLE_PROCESSED_COLUMNS.has(sectionId),
            sortable: true,
            align: 'left' as const,
            headerAlign: 'left' as const,
            // Backend always returns date fields in ISO format (YYYY-MM-DD)
            getSortComparator: isDateField(sectionId) ? createDateSortComparator('YYYY-MM-DD') : getNumericStringSortComparator,
            renderCell: (params: GridRenderCellParams) => {
              const value = params.value;
              const instrumentType = params.row._instrument_type;
              const uiType = instrumentType ? getUITypeFromBackend(instrumentType) : null;
              const isMandatoryEmpty = isMandatoryFieldEmpty(
                sectionId,
                value,
                uiType
              ) || isAwardFieldMissing(sectionId, uiType, params.row);
              const { isEdited, originalValue } = getEditInfo(
                params.row,
                sectionId,
                value
              );

              return (
                <EditableCell
                  showWarning={isMandatoryEmpty}
                  isEdited={isEdited}
                  originalValue={originalValue}
                  warningType={getWarningType(params.row, sectionId)}
                >
                  <Typography sx={{ fontSize: '0.875rem' }}>{value}</Typography>
                </EditableCell>
              );
            },
          };
        });

      return [...metadataColumns, ...dataColumns];
    }

    return [];
  }, [
    rows,
    viewMode,
    appliedTemplateConfig,
    rowsType,
    instrumentTypeColumn,
    uploadStatusColumn,
    matchReasonColumn,
    linkedAccountColumn,
    renderCellWithErrorHighlight,
    autosizeColumns,
    hidePreviewColumns,
    allowCellEditing,
    currencyColumn,
    buySellColumn,
    settlementDateColumn,
  ]);

  return { columns };
};
